// Core packages

// Third party packages
import moment from "moment";

// Custom packages
import { deadlinesV3BackEndMessage } from "src/app/modules/deadlines-v3/deadlines-v3.utils";
import { labelOf } from "src/app/modules/properties/document-archive-insert.utils";
import { archiveGroupLabel } from "src/app/modules/properties/document-archive-row-actions.utils";
import { documentArchiveQrDocUrl } from "src/app/modules/properties/document-archive-qr-print.utils";
import { isFreeArchivePlan } from "src/app/modules/properties/document-archive-visibility.utils";
import { isArchiveObjectId } from "src/app/modules/search-docs/document-archive-general-search.utils";
import {
  DocumentArchiveTreeArea,
  DocumentArchiveTreeRow,
} from "src/app/shared/interfaces/document-archive-doc.interface";
import { DocumentArchiveRasDoc } from "src/app/shared/interfaces/document-archive-export.interface";

// Lives with the other QR addresses; re-exported for the callers that read it from here
export { documentArchiveQrDocUrl };

/**
 * Pure helpers of the «Esporta» menu of the dossier on the 3.0.0 archive page (`documentsV3`), FE-M8.
 *
 * The legacy dossier writes these rules inline in its template (`property-documents.page.html`,
 * `#exportMenu`); they are gathered here so the page template only reads the result and the rules
 * can be tested without Angular. The back-end does NOT check the plan: hiding an entry from the free
 * plans is the job of these rules alone.
 *
 * The four entries of the legacy menu: «Esporta inseriti» and «Esporta mancanti» are Excels the
 * back-end writes; «Esporta RAS» (a PDF) and «Esporta contratti» (a Word file) are composed here, from
 * the rows of `/ras` and of `/tree`, with the QR code of each document on the address of NEW-A3.
 *
 * @since 3.0.0
 */

/**
 * Role types the legacy dossier shows the whole «Esporta» menu to: its `loggedUserAdmin` (admin,
 * operator) or `loggedUserMaster` (master, collaborator). The guests never see it.
 */
const EXPORT_MENU_ROLE_TYPES = ["admin", "operator", "master", "collaborator"];

/**
 * Role types «Esporta contratti» is shown to: the legacy `loggedUserRoleType === 'admin' ||
 * loggedUserRoleType === 'operator'`, the two the legacy route of the contracts answered.
 */
const CONTRACTS_EXPORT_ROLE_TYPES = ["admin", "operator"];

/**
 * Pages of the RAS PDF the legacy counts before the tables, and the rows it counts per page: four
 * when the area changes, seven for the last area. Both kept as they are, so the cover prints the
 * same total the legacy prints.
 */
const RAS_FIRST_PAGES = 2;
const RAS_ROWS_PER_PAGE = 4;
const RAS_ROWS_PER_LAST_PAGE = 7;

/** The day before which a document of a `reevaluated` type is printed as re-evaluated */
const RAS_REEVALUATED_BEFORE = "2000-01-01";

/** Permission behind both Excels, on the legacy menu and on the back-end alike */
const EXPORT_PERMISSION = "canExportProperties";

/** A value as printable text: strings and numbers as they are, anything else `""` */
function textOf(value: unknown): string {
  return typeof value === "string" || typeof value === "number" ? String(value) : "";
}

/** A row the contracts export prints. @see documentArchiveContractItems */
function isExportableContract(row: DocumentArchiveTreeRow | null | undefined): boolean {
  return (
    !!row &&
    isArchiveObjectId(row._id) &&
    row.contract === true &&
    row.present === true &&
    row.mockup !== true &&
    row.deleted !== true &&
    row.expired !== true
  );
}

/**
 * The supplier of a contract outside the aggregate: the stored name, unless what is stored is the id
 * of a registered supplier, which is not something to print.
 */
function supplierNameOf(row: DocumentArchiveTreeRow): string {
  const data: { [key: string]: unknown } = row.data && typeof row.data === "object" ? row.data : {};
  const stored = textOf(data.ragioneSocialeFornitore);
  return isArchiveObjectId(stored) ? "" : stored;
}

/** Pages taken by `count` rows at `perPage` rows a page, the last one partial */
function pagesFor(count: number, perPage: number): number {
  return Math.floor(count / perPage) + (count % perPage > 0 ? 1 : 0);
}

/**
 * Whether a document date falls before 2000, as the legacy dialog decides it: a date moment can read
 * (`YYYY-MM-DD`, or ISO with the time), compared by days.
 */
function isBefore2000(dataDocumento: string): boolean {
  if (!dataDocumento) {
    return false;
  }
  const date = moment(dataDocumento);
  return date.isValid() && date.diff(moment(RAS_REEVALUATED_BEFORE), "days") < 0;
}

/** Media type of the two Excels */
export const DOCUMENT_ARCHIVE_XLSX_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/**
 * The two Excels of the menu, by the last segment of their back-end route
 * (`GET /document-archive-docs/export/:target`).
 */
export type DocumentArchiveExportTarget = "inserted" | "missing";

/**
 * Translation key of the file name of each Excel: the very keys of the legacy dossier, so the file
 * keeps its name («Immobili_doc_inseriti …», «Immobili_doc_mancanti …»).
 */
const FILE_NAME_KEYS: { [target in DocumentArchiveExportTarget]: string } = {
  inserted: "properties.list.insertedDocs2025",
  missing: "properties.list.missingDocs",
};

/**
 * Who is asking for the menu, as far as its rules care.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveExportViewer {
  /** Whether this build puts the 3.0.0 archive live (@see isArchiveV3Live) */
  archiveV3Live: boolean;
  /** Type of the active role, `""` when not known yet */
  roleType: string;
  /** Permissions of the active role */
  permissions: string[];
  /**
   * `subscription.key` of the property: `""` for a property with no plan, which the legacy treats as
   * a paid one; `null` while the property has not answered, or when it could not be read.
   */
  subscriptionKey: string | null;
}

/**
 * The entries of the menu this viewer gets.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveExportMenu {
  /** «Esporta inseriti» */
  inserted: boolean;
  /** «Esporta mancanti» */
  missing: boolean;
  /** «Esporta RAS» */
  ras: boolean;
  /** «Esporta contratti» */
  contracts: boolean;
}

/**
 * The entries of the «Esporta» menu for this viewer: the legacy rules, behind the switch.
 *
 *  - nothing at all before the switch (`archiveV3Live`): until then the routes read the new archive
 *    only, and miss the rows filed in the legacy one after the copy of 10 August 2026; and the QR
 *    codes of RAS and contracts point to the public page of the new archive (NEW-A3);
 *  - nothing for a role type outside admin, operator, master, collaborator;
 *  - «Esporta inseriti»: `canExportProperties`;
 *  - «Esporta mancanti»: `canExportProperties` and a plan other than FREE and FREEBASIC;
 *  - «Esporta RAS»: a plan other than FREE and FREEBASIC, with NO permission: the legacy menu asks
 *    none, and the back-end is aligned with it (decision of 26 October 2026);
 *  - «Esporta contratti»: admin or operator.
 *
 * ⚠️ A PLAN NOT KNOWN YET HIDES «MANCANTI» AND «RAS», as it hides every paid tool of this page
 * (@see documentArchiveVisibility): showing them until the property answers would flash them in front
 * of a free customer. The legacy defaults to a paid plan there instead.
 *
 * @since 3.0.0
 *
 * @param viewer Who is asking
 */
export function documentArchiveExportMenu(viewer: DocumentArchiveExportViewer): DocumentArchiveExportMenu {
  const none: DocumentArchiveExportMenu = { inserted: false, missing: false, ras: false, contracts: false };
  if (!viewer || viewer.archiveV3Live !== true) {
    return none;
  }
  const roleType = typeof viewer.roleType === "string" ? viewer.roleType : "";
  if (EXPORT_MENU_ROLE_TYPES.indexOf(roleType) === -1) {
    return none;
  }
  const permissions = Array.isArray(viewer.permissions) ? viewer.permissions : [];
  const canExport = permissions.indexOf(EXPORT_PERMISSION) > -1;
  const subscriptionKey = viewer.subscriptionKey;
  const paidPlan = typeof subscriptionKey === "string" && !isFreeArchivePlan(subscriptionKey);
  return {
    inserted: canExport,
    missing: canExport && paidPlan,
    ras: paidPlan,
    contracts: CONTRACTS_EXPORT_ROLE_TYPES.indexOf(roleType) > -1,
  };
}

/**
 * Whether the menu has at least one entry: an empty menu is not shown at all.
 *
 * @since 3.0.0
 *
 * @param menu The entries
 */
export function hasDocumentArchiveExportMenu(menu: DocumentArchiveExportMenu): boolean {
  return !!menu && (menu.inserted || menu.missing || menu.ras || menu.contracts);
}

/**
 * Who is asking for the export entries of a legacy page, the switch aside (@see legacyPageExportMenu).
 *
 * @since 3.0.0
 */
export type LegacyPageExportViewer = Pick<DocumentArchiveExportViewer, "roleType" | "permissions" | "subscriptionKey">;

/**
 * The export entries of a legacy page that still offers them once the switch is live — the «SCARICA»
 * column of the property home, the «Esporta» menu of the mapping, the property list of the
 * statistics — as far as the switch decides them:
 *  - until the switch, every entry: the page shows each one under its own rules, as it always has,
 *    and calls the legacy routes;
 *  - once it is live, only the entries of the «Esporta» menu of the 3.0.0 dossier, under its rules
 *    (@see documentArchiveExportMenu), since the page then calls the same routes (decision of
 *    3 November 2026: every such page, with the rules of the dossier menu).
 *
 * @since 3.0.0
 *
 * @param live   Whether the switch is live (@see isArchiveV3Live)
 * @param viewer Who is asking
 */
export function legacyPageExportMenu(live: boolean, viewer: LegacyPageExportViewer): DocumentArchiveExportMenu {
  if (live !== true) {
    return { inserted: true, missing: true, ras: true, contracts: true };
  }
  return documentArchiveExportMenu({
    archiveV3Live: true,
    roleType: viewer ? viewer.roleType : "",
    permissions: viewer ? viewer.permissions : [],
    subscriptionKey: viewer ? viewer.subscriptionKey : null,
  });
}

/**
 * The Excel of the 3.0.0 archive that stands in for a legacy export target once the switch is live:
 * `insertedDocs2025` («Esporta inseriti») and `missingDocs` («Esporta mancanti»). `null` for any
 * other target, which keeps its legacy route.
 *
 * @since 3.0.0
 *
 * @param legacyTarget The last segment of the legacy route `GET properties/:id/export/:target`
 */
export function documentArchiveExportTargetOf(legacyTarget: unknown): DocumentArchiveExportTarget | null {
  if (legacyTarget === "insertedDocs2025") {
    return "inserted";
  }
  if (legacyTarget === "missingDocs") {
    return "missing";
  }
  return null;
}

/**
 * Translation key of the file name of an Excel. @see FILE_NAME_KEYS
 *
 * @since 3.0.0
 *
 * @param target Which Excel
 */
export function documentArchiveExportFileNameKey(target: DocumentArchiveExportTarget): string {
  return FILE_NAME_KEYS[target];
}

/**
 * File name of an Excel: the translated prefix and the local time to the second, as the legacy
 * composes it («Immobili_doc_inseriti 20261026143005.xlsx»). The back-end sends no name of its own.
 *
 * The hour is on 24 hours. The legacy formats it with `hh` and gives two files of 2 a.m. and 2 p.m.
 * the same name.
 *
 * @since 3.0.0
 *
 * @param prefix The translated prefix
 * @param now    The moment of the download
 */
export function documentArchiveExportFileName(prefix: string, now: Date): string {
  return `${prefix} ${moment(now).format("YYYYMMDDHHmmss")}.xlsx`;
}

/**
 * The message of the back-end in an error whose body came as a Blob, read as text; `""` when there
 * is none, or when the body cannot be read. Never rejects.
 *
 * Both Excels are asked for as a Blob, so their error envelope comes as a Blob as well.
 *
 * @since 3.0.0
 *
 * @param err The error emitted by ApiService
 */
export function documentArchiveExportErrorMessage(err: unknown): Promise<string> {
  const body = err && typeof err === "object" ? (err as { error?: unknown }).error : null;
  if (!(body instanceof Blob)) {
    return Promise.resolve(deadlinesV3BackEndMessage(body));
  }
  return new Promise<string>((resolve) => {
    const reader = new FileReader();
    reader.onload = (): void =>
      resolve(deadlinesV3BackEndMessage(typeof reader.result === "string" ? reader.result : ""));
    reader.onerror = (): void => resolve("");
    reader.readAsText(body);
  });
}

/**
 * The address on the cover of the RAS, as the legacy dialog composes it:
 * «<route> <streetNumber> - <postalCode> <city>». A part that is missing is left empty, where the
 * legacy printed "undefined".
 *
 * @since 3.0.0
 *
 * @param address `properties.address`, as the property service returns it
 */
export function documentArchiveRasAddress(address: unknown): string {
  const parts: { [key: string]: unknown } =
    address && typeof address === "object" ? (address as { [key: string]: unknown }) : {};
  const text = (value: unknown): string =>
    typeof value === "string" || typeof value === "number" ? String(value) : "";
  return `${text(parts.route)} ${text(parts.streetNumber)} - ${text(parts.postalCode)} ${text(parts.city)}`;
}

/**
 * One line of the RAS PDF, in the shape the PDF of the legacy dialog reads
 * (`ExportRasDocsDialogComponent.onExportFile`), which the 3.0.0 dialog reuses as it is.
 *
 * `qrcode` is what tells the two kinds of line apart, as in the legacy: a document filed has one, a
 * type with nothing filed has `""` and prints the urgent or the necessary mark instead.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveRasPdfItem {
  /** Progressive from 1, the id of the hidden QR code element the PDF reads the image from */
  index: number;
  /** The area: the PDF opens a new table on every change */
  docSection: string;
  /** The type, as printed */
  type: string;
  /** The block, as printed */
  structureObjectName: string;
  ragioneSocialeFornitore: string;
  dataDocumento: string;
  dateExpired: string;
  /** Address of the QR code of the document, `""` for a type with nothing filed */
  qrcode: string;
  /** A document of a `reevaluated` type dated before 2000: no QR code, the re-evaluated mark */
  reevaluated?: boolean;
  /** On a type with nothing filed only: the urgent mark instead of the necessary one */
  urgent?: boolean;
}

/**
 * The lines of the RAS PDF and the page count of its cover, from the rows of `GET /ras`: the loop of
 * the legacy dialog, with the QR code on the address of NEW-A3 (@see documentArchiveQrDocUrl).
 *
 * The page count is the legacy formula, kept as it is so the cover prints the same number: two
 * pages, plus the rows of every area but the last at four per page, plus the rows of the last at
 * seven per page.
 *
 * A row of `data` without a valid `_id` is not a document the PDF can print a QR code for: it is
 * skipped, and a type left with no row is printed as missing.
 *
 * @since 3.0.0
 *
 * @param rasDocs `data.rasDocs` of the answer
 * @param host    `ConfigService.frontEndHost`
 */
export function documentArchiveRasPdfItems(
  rasDocs: DocumentArchiveRasDoc[] | null | undefined,
  host: string
): { items: DocumentArchiveRasPdfItem[]; totalPages: number } {
  const items: DocumentArchiveRasPdfItem[] = [];
  let totalPages = RAS_FIRST_PAGES;
  let currentSection = "";
  let countInSection = 0;

  (Array.isArray(rasDocs) ? rasDocs : []).forEach((rasDoc) => {
    if (!rasDoc || typeof rasDoc !== "object") {
      return;
    }
    const docSection = textOf(rasDoc.titleLevel1);
    const type = textOf(rasDoc.docTitle);
    const structureObjectName = textOf(rasDoc.titleLevel3);

    if (currentSection !== docSection) {
      currentSection = docSection;
      totalPages += pagesFor(countInSection, RAS_ROWS_PER_PAGE);
      countInSection = 0;
    }

    const rows = (Array.isArray(rasDoc.data) ? rasDoc.data : []).filter(
      (row) => !!row && isArchiveObjectId(row._id)
    );
    if (rows.length === 0) {
      countInSection += 1;
      items.push({
        index: items.length + 1,
        docSection,
        type,
        structureObjectName,
        ragioneSocialeFornitore: "",
        dataDocumento: "",
        dateExpired: "",
        qrcode: "",
        urgent: rasDoc.urgent === true,
      });
      return;
    }

    rows.forEach((row) => {
      const data: { [key: string]: unknown } = row.data && typeof row.data === "object" ? row.data : {};
      const dataDocumento = textOf(data.dataDocumento);
      countInSection += 1;
      items.push({
        index: items.length + 1,
        docSection,
        type,
        structureObjectName,
        ragioneSocialeFornitore: textOf(data.ragioneSocialeFornitore),
        dataDocumento,
        dateExpired: textOf(row.dateExpired),
        qrcode: documentArchiveQrDocUrl(host, row._id),
        reevaluated: rasDoc.reevaluated === true && isBefore2000(dataDocumento),
      });
    });
  });

  totalPages += pagesFor(countInSection, RAS_ROWS_PER_LAST_PAGE);
  return { items, totalPages };
}

/**
 * One line of the Word file of the contracts, in the shape the legacy dialog reads
 * (`ExportContractsDialogComponent.onExportFile`), which the 3.0.0 dialog reuses as it is.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveContractItem {
  /** Progressive from 0, the id of the QR code element the Word file reads the image from */
  index: number;
  /** `_id` of the row */
  rowId: string;
  /** The type, as printed */
  type: string;
  /** The element of the mapping the contract belongs to, `""` for a general contract */
  structureObjectName: string;
  data: {
    ragioneSocialeFornitore: string;
    dataDocumento: string;
  };
  dateExpired: string;
  /** Address of the QR code of the document (@see documentArchiveQrDocUrl) */
  qrcode: string;
}

/**
 * The contracts to export, from the tree: the rows the legacy `document-archives/:id/contracts` gave
 * and the legacy dialog kept — `contract`, filed, not demonstrative, not deleted, not expired — each
 * once, in tree order (confirmed by the back-end on 26 October 2026).
 *
 * Read over the WHOLE tree and not off the contracts section alone: that section aggregates the
 * contracts of the other areas but leaves out the tenancy contracts and `RF4`, which the legacy
 * export printed. So a contract met twice — in its own area and in the aggregate, same `_id` — is
 * kept once, at its first place, and takes from the aggregated copy the two fields only that copy
 * carries resolved (recognised by `executorName`, @see DocumentArchiveTreeRow):
 *  - the element: `structureLabel`; on a row that is not in the aggregate, the name of the element
 *    block it sits in, `""` anywhere else;
 *  - the supplier: `executorName`; on a row that is not in the aggregate, `data.ragioneSocialeFornitore`
 *    when it is a name and not the id of a supplier, `""` otherwise. The legacy printed the stored
 *    value, which is an ObjectId on most rows.
 *
 * @since 3.0.0
 *
 * @param areas `areas` of the tree
 * @param lang  Language of the page
 * @param host  `ConfigService.frontEndHost`
 */
export function documentArchiveContractItems(
  areas: DocumentArchiveTreeArea[] | null | undefined,
  lang: string,
  host: string
): DocumentArchiveContractItem[] {
  const items: DocumentArchiveContractItem[] = [];
  const byRowId: { [rowId: string]: { item: DocumentArchiveContractItem; resolved: boolean } } = {};

  (Array.isArray(areas) ? areas : []).forEach((area) => {
    const contents = area && Array.isArray(area.content) ? area.content : [];
    contents.forEach((content) => {
      const groups = content && Array.isArray(content.groups) ? content.groups : [];
      groups.forEach((group) => {
        const blockName = group && group.source === "structureObject" ? archiveGroupLabel(group, lang) : "";
        const types = group && Array.isArray(group.documents) ? group.documents : [];
        types.forEach((docType) => {
          const rows = docType && Array.isArray(docType.archiveDocs) ? docType.archiveDocs : [];
          rows.forEach((row) => {
            if (!isExportableContract(row)) {
              return;
            }
            const resolved = typeof row.executorName === "string";
            const structureObjectName =
              typeof row.structureLabel === "string" ? row.structureLabel : blockName;
            const supplier = resolved ? row.executorName : supplierNameOf(row);

            const known = byRowId[row._id];
            if (known) {
              if (resolved && !known.resolved) {
                known.item.structureObjectName = structureObjectName;
                known.item.data.ragioneSocialeFornitore = supplier;
                known.resolved = true;
              }
              return;
            }

            const data: { [key: string]: unknown } = row.data && typeof row.data === "object" ? row.data : {};
            const item: DocumentArchiveContractItem = {
              index: items.length,
              rowId: row._id,
              type: labelOf(docType.translation, lang),
              structureObjectName,
              data: {
                ragioneSocialeFornitore: supplier,
                dataDocumento: textOf(data.dataDocumento),
              },
              dateExpired: textOf(row.dateExpired),
              qrcode: documentArchiveQrDocUrl(host, row._id),
            };
            byRowId[row._id] = { item, resolved };
            items.push(item);
          });
        });
      });
    });
  });

  return items;
}

/**
 * Translation key of the message the RAS and contracts dialogs show when their rows cannot be read:
 * 403 has a message of its own, a failure without status (`Error`, status 0) is the network, anything
 * else the generic one.
 *
 * @since 3.0.0
 *
 * @param err The error, or the `Error` VALUE ApiService emits on a network failure
 */
export function documentArchiveExportLoadErrorKey(err: unknown): string {
  if (err instanceof Error) {
    return "searchDocs.v3.messages.network";
  }
  const status = err && typeof err === "object" ? (err as { status?: unknown }).status : undefined;
  if (status === 0) {
    return "searchDocs.v3.messages.network";
  }
  if (status === 403) {
    return "properties.documentsV3.export.forbidden";
  }
  return "properties.documentsV3.export.error";
}
